import React, { FC, ReactNode } from 'react';
import { BasicContainer, ButtonAsWrapper, FlexCol, GridDynamic, NamedButtonProps, TextDynamic } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import Modal from '@minecraft.modal';
import { ImageCarouselFill } from '@minecraft.molecules';
import { WindowWidthContext } from '@minecraft.utils';
import { HEADSHOT_GALLERY_IMAGES } from './headshotImages';

const HEADSHOTS_MODAL_TITLE_ID = 'headshots-modal-title';

/** Full-width sizing shared by every headshots modal call to action. */
export const HEADSHOTS_MODAL_ACTION_PROPS: Omit<NamedButtonProps, 'label' | 'onClick'> & { 'data-testid': string } = {
  full: true,
  block: true,
  borderRadius: 'sm',
  className: 'cn_box-sizing_border-box',
  mdProps: { full: true, block: true },
  lgProps: { full: true, block: true },
  'data-testid': 'headshots-info-modal-primary-action',
};

export type HeadshotsModalLayoutProps = {
  title: string;
  /** Rendered directly under the title. */
  summary: ReactNode;
  /** Rendered below the summary, each top-level element spaced apart. */
  details: ReactNode;
  /** Calls to action at the bottom of the modal. */
  actions: ReactNode;
  closeLabel: string;
  onClose: () => void;
};

/**
 * Frame shared by the headshots modals: the sample gallery, the close button, and
 * the content column. Full height on mobile with the actions pinned to the bottom.
 */
export const HeadshotsModalLayout: FC<HeadshotsModalLayoutProps> = ({
  title,
  summary,
  details,
  actions,
  closeLabel,
  onClose,
}) => {
  const { lessMd } = WindowWidthContext.useWidthData();

  return (
    <Modal
      isOpen
      isHeaderHidden
      isFooterHidden
      isCloseHidden
      noPadding
      bodyProps={{ className: 'cn_atom_p-0' }}
      height={lessMd ? 'full' : 'auto'}
      modalSize="medium"
      labelledBy={HEADSHOTS_MODAL_TITLE_ID}
      handleToggleClick={onClose}
      data-testid="headshots-info-modal"
    >
      <GridDynamic
        className="cn_relative cn_overflow-hidden"
        w="full"
        h="full"
        columns="1fr"
        rows="auto 1fr"
        bgColor="card"
        borderRadius="sm"
        alignItems="stretch"
        mdProps={{ columns: '1fr 1fr', rows: 'auto' }}
      >
        <FlexCol w="full" h="52" grow={0} bgColor="cardInverted" mdProps={{ h: 'full', minHeight: 39.1875 }}>
          <ImageCarouselFill images={HEADSHOT_GALLERY_IMAGES} autoplay data-testid="headshots-modal-gallery" />
        </FlexCol>
        <FlexCol
          w="full"
          grow={0}
          padding="6"
          className="cn_box-sizing_border-box"
          mdProps={{
            padding: undefined,
            className: 'cn_box-sizing_border-box cn_atom_px-8 cn_atom_pt-8 cn_atom_pb-8',
          }}
        >
          {!lessMd && (
            <>
              <BasicContainer h="8" />
              <BasicContainer h="4" />
            </>
          )}
          <FlexCol gap="8" w="full" grow={0}>
            <FlexCol gap="2" w="full" grow={0}>
              <TextDynamic
                id={HEADSHOTS_MODAL_TITLE_ID}
                as="h2"
                fontWeight="medium"
                fontSize="h3"
                mdProps={{ fontSize: 'h2', fontWeight: 'medium' }}
              >
                {title}
              </TextDynamic>
              {summary}
            </FlexCol>
            {details}
          </FlexCol>
          {lessMd ? (
            <FlexCol grow={1} minHeight={2} />
          ) : (
            <>
              <BasicContainer h="10" />
              <BasicContainer h="8" />
            </>
          )}
          <FlexCol
            gap="2"
            w="full"
            grow={0}
            alignItems="center"
            className="cn_box-sizing_border-box"
            mdProps={{ className: 'cn_box-sizing_border-box cn_atom_px-3' }}
          >
            {actions}
          </FlexCol>
        </FlexCol>
        <BasicContainer className="cn_absolute" top={1} right={1} zIndex={2}>
          <ButtonAsWrapper aria-label={closeLabel} onClick={onClose} noPadding data-testid="headshots-info-modal-close">
            <Icon name="close" size="sm" themeColor={lessMd ? 'inverted' : 'active'} />
          </ButtonAsWrapper>
        </BasicContainer>
      </GridDynamic>
    </Modal>
  );
};

export default HeadshotsModalLayout;
